import API from '@services/API';
import { setError, startSpinner, stopSpinner } from '../common/actionCreators';
import { NAV_BAR_TYPES } from '@constants/actionTypes/navBarActionTypes';
import { LEFT_NAV_TYPES } from '@constants/actionTypes/leftNavTypes';
import {
    DELEGATION_HISTORY_TYPES,
    DELEGATION_FILTERS_TYPES,
    DELEGATION_PAGINATION_TYPES,
} from '@constants/actionTypes/delegationActionTypes';
import { Dispatch } from 'redux';

// Type for Redux getState function - replace 'unknown' with RootState when available
type GetState = () => unknown;

/**
 * Represents delegation history data from the server.
 * Contains historical records of delegation actions performed by users.
 *
 * @example
 * {
 *   history: [
 *     { id: '123', timestamp: '2024-01-15', action: 'delegate', userId: '456' }
 *   ]
 * }
 */
export interface IDelegationHistory {
    [key: string]: unknown;
}

/**
 * Filter values for delegation history search.
 * Used to filter delegation records by various criteria.
 *
 * @example
 * {
 *   dateFrom: '2024-01-01',
 *   dateTo: '2024-12-31',
 *   status: 'active',
 *   searchText: 'user@example.com'
 * }
 */
export interface IFiltersValues {
    [key: string]: unknown;
}

/**
 * Represents a single filter value change.
 *
 * @property name - The filter field name
 * @property value - The new filter value
 */
export interface IFilterValue {
    name: string;
    value: unknown;
}

/**
 * Navigation bar data - typically an array of menu items.
 * Used by the navBarReducer which expects the data directly.
 */
export type INavBarData = unknown[];

/**
 * Left navigation parameters.
 * Configuration for the left navigation component.
 */
export interface ILeftNavParams {
    [key: string]: unknown;
}

/**
 * API response structure for delegation data endpoint.
 *
 * @property responseCode - HTTP status code (200 for success)
 * @property designationHistory - Historical delegation data
 * @property count - Total number of delegation records
 */
interface IDelegationDataResponse {
    responseCode?: number;
    designationHistory?: IDelegationHistory;
    count?: number;
    [key: string]: unknown;
}

/**
 * API response structure for navigation bar data endpoint.
 *
 * @property menu - Array of navigation menu items
 */
interface INavBarDataResponse {
    menu?: unknown[];
    [key: string]: unknown;
}

// Action Interfaces
interface ISetDelegationHistoryAction {
    type: typeof DELEGATION_HISTORY_TYPES.SET_DELEGATION_HISTORY;
    delegationHistory: IDelegationHistory;
}

interface IInitDelegationFiltersAction {
    type: typeof DELEGATION_FILTERS_TYPES.INIT_ALL_FILTERS;
    filtersValues: IFiltersValues;
}

interface ISetFilterValueAction {
    type: typeof DELEGATION_FILTERS_TYPES.SET_FILTER_VALUE;
    newFilterValue: IFilterValue;
}

interface IResetFilterValuesAction {
    type: typeof DELEGATION_FILTERS_TYPES.RESET_FILTER_VALUES;
    filtersInitialValues: IFiltersValues;
}

interface ISetPaginationCountAction {
    type: typeof DELEGATION_PAGINATION_TYPES.SET_COUNT;
    count: number;
}

interface ISetNavBarDataAction {
    type: typeof NAV_BAR_TYPES.SET_NAV_BAR_DATA;
    navBarData: INavBarData;
}

interface ISetLeftNavParamsAction {
    type: typeof LEFT_NAV_TYPES.SET_LEFT_NAV_PARAMS;
    leftNavParams: ILeftNavParams;
}

/**
 * Union type of all delegation-related actions for use in reducers.
 */
export type DelegationAction =
    | ISetDelegationHistoryAction
    | IInitDelegationFiltersAction
    | ISetFilterValueAction
    | IResetFilterValuesAction
    | ISetPaginationCountAction
    | ISetNavBarDataAction
    | ISetLeftNavParamsAction;

// Synchronous Action Creators
export const setDelegationHistory = (delegationHistory: IDelegationHistory): ISetDelegationHistoryAction => ({
    type: DELEGATION_HISTORY_TYPES.SET_DELEGATION_HISTORY,
    delegationHistory,
});

export const initDelegationFilters = (filtersValues: IFiltersValues): IInitDelegationFiltersAction => ({
    type: DELEGATION_FILTERS_TYPES.INIT_ALL_FILTERS,
    filtersValues,
});

export const setFilterValue = (newFilterValue: IFilterValue): ISetFilterValueAction => ({
    type: DELEGATION_FILTERS_TYPES.SET_FILTER_VALUE,
    newFilterValue,
});

export const resetFilterValues = (filtersInitialValues: IFiltersValues): IResetFilterValuesAction => ({
    type: DELEGATION_FILTERS_TYPES.RESET_FILTER_VALUES,
    filtersInitialValues,
});

export const setPaginationCount = (count: number): ISetPaginationCountAction => ({
    type: DELEGATION_PAGINATION_TYPES.SET_COUNT,
    count,
});

export const setNavBarData = (navBarData: INavBarData): ISetNavBarDataAction => ({
    type: NAV_BAR_TYPES.SET_NAV_BAR_DATA,
    navBarData,
});

export const setLeftNavParams = (leftNavParams: ILeftNavParams): ISetLeftNavParamsAction => ({
    type: LEFT_NAV_TYPES.SET_LEFT_NAV_PARAMS,
    leftNavParams,
});

/************************

 # Async action creators

 ************************/

const actions = { setError, startSpinner, stopSpinner };

declare const global: {
    __ENV__: {
        PRODUCTION: boolean;
    };
};

export const fetchDelegationData = (url: string) => async (dispatch: Dispatch, getState: GetState) => {
    try {
        const data: IDelegationDataResponse = await API.getData(url, dispatch, actions, true, getState());
        if (data?.responseCode === 200 && data.designationHistory !== undefined && data.count !== undefined) {
            dispatch(setDelegationHistory(data.designationHistory));
            dispatch(setPaginationCount(data.count));
        } else {
            !global.__ENV__.PRODUCTION && console.log('DelegationHistory Failure', {
                url,
                responseCode: data?.responseCode,
                hasDesignationHistory: data?.designationHistory !== undefined,
                hasCount: data?.count !== undefined,
            });
        }
    } catch (error) {
        !global.__ENV__.PRODUCTION && console.log('fetchDelegationData error', {
            url,
            error: error instanceof Error ? error.message : 'Unknown error',
        });
    }
};

export const fetchNavBarData = (url: string) => async (dispatch: Dispatch) => {
    try {
        const data: INavBarDataResponse = await API.getData(url, dispatch, actions);
        if (data?.menu) {
            dispatch(setNavBarData(data.menu));
        }
    } catch (error) {
        !global.__ENV__.PRODUCTION && console.log('fetchNavBarData error', {
            url,
            error: error instanceof Error ? error.message : 'Unknown error',
        });
    }
};

export const fetchLeftNavParams = (url: string) => async (dispatch: Dispatch, getState: GetState) => {
    try {
        const data: ILeftNavParams = await API.getData(url, dispatch, actions, true, getState());
        if (data) {
            dispatch(setLeftNavParams(data));
        }
    } catch (error) {
        !global.__ENV__.PRODUCTION && console.log('fetchLeftNavParams error', {
            url,
            error: error instanceof Error ? error.message : 'Unknown error',
        });
    }
};
